import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import {
  approveAdminPendingMarketplaceProduct,
  getAdminMarketplaceProduct,
  rejectAdminPendingMarketplaceProduct,
} from "../../../features/marketplace/adminMarketplaceApi";
import {
  CATEGORY_LABELS,
  MODERATION_STATUSES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import AdminAvatar from "@/assets/UI/admin.webp";
import cls from "./AdminPendingProductDetailPage.module.scss";

const getClubLink = (club) => {
  if (!club?._id) return "#";
  if (!club.cityId || !club.streetId) return `/admin/clubs/single/${club._id}`;
  return `/admin/cities/single/${club.cityId}/streets/${club.streetId}/clubs/${club._id}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export default function AdminPendingProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState("");
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [activeModerationForm, setActiveModerationForm] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getAdminMarketplaceProduct(productId);
        if (!mounted) return;
        setProduct(data?.product || null);
        setApproveNotes(data?.product?.moderationNotes || "");
        setRejectReason(data?.product?.moderationRejectionReason || "");
        setRejectNotes(data?.product?.moderationNotes || "");
      } catch (err) {
        if (!mounted) return;
        setProduct(null);
        setError(err?.response?.data?.msg || "Failed to load product.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [productId]);

  const mediaItems = useMemo(() => {
    if (!product) return [];

    return [
      ...(product.listingImages || []).map((item, index) => ({
        id: `img-${item?.key || index}`,
        type: "image",
        url: item?.url,
      })),
      ...(product.listingVideos || []).map((item, index) => ({
        id: `vid-${item?.videoId || index}`,
        type: "video",
        url: item?.url,
        videoId: item?.videoId,
      })),
    ].filter((item) => item.url);
  }, [product]);

  const handleApprove = async () => {
    if (!canModerate || submitting) return;
    setActionError("");
    setActiveModerationForm("approve");
  };

  const handleApproveSubmit = async () => {
    if (!product?._id || submitting) return;

    setSubmitting("approve");
    setActionError("");

    try {
      await approveAdminPendingMarketplaceProduct(product._id, {
        notes: String(approveNotes || "").trim() || undefined,
      });
      setActiveModerationForm("");
      navigate("/admin/marketplace/pending-products");
    } catch (err) {
      setActionError(err?.response?.data?.msg || "Failed to approve product.");
    } finally {
      setSubmitting("");
    }
  };

  const handleReject = async () => {
    if (!canModerate || submitting) return;
    setActionError("");
    setActiveModerationForm("reject");
  };

  const handleRejectSubmit = async () => {
    if (!product?._id || submitting) return;

    setSubmitting("reject");
    setActionError("");

    try {
      await rejectAdminPendingMarketplaceProduct(product._id, {
        reason: String(rejectReason || "").trim() || undefined,
        notes: String(rejectNotes || "").trim() || undefined,
      });
      setActiveModerationForm("");
      navigate("/admin/marketplace/pending-products");
    } catch (err) {
      setActionError(err?.response?.data?.msg || "Failed to reject product.");
    } finally {
      setSubmitting("");
    }
  };

  if (loading) {
    return (
      <section className={cls.page}>
        <div className={cls.stateShell}>
          <div className={cls.stateCard}>Loading pending product...</div>
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className={cls.page}>
        <div className={cls.pageInner}>
          <Link
            to="/admin/marketplace/pending-products"
            className={cls.backLink}
          >
            <span className={cls.backIcon}>←</span>
            <span>Back to pending products</span>
          </Link>

          <div className={cls.errorCard}>
            {error || "Pending product not found."}
          </div>
        </div>
      </section>
    );
  }

  const subtypeValue = product.serviceType || product.digitalType || "N/A";
  const moderationStatus =
    product.moderationStatus || MODERATION_STATUSES.PENDING;
  const isApproved = moderationStatus === MODERATION_STATUSES.APPROVED;
  const isRejected = moderationStatus === MODERATION_STATUSES.REJECTED;
  const canModerate = moderationStatus === MODERATION_STATUSES.PENDING;
  const moderator = product.moderationReviewedBy || null;
  const moderatorAvatarUrl = moderator?.isAdmin
    ? AdminAvatar
    : moderator?.avatarUrl || AdminAvatar;

  return (
    <section className={cls.page}>
      <div className={cls.pageInner}>
        <Link to="/admin/marketplace/pending-products" className={cls.backLink}>
          <span className={cls.backIcon}>←</span>
          <span>Back to pending products</span>
        </Link>

        <header className={cls.heroCard}>
          <div className={cls.heroGlow} />

          <div className={cls.heroTop}>
            <div className={cls.heroText}>
              <p className={cls.kicker}>Marketplace Moderation Console</p>
              <h1 className={cls.title}>
                {product.title || "Untitled product"}
              </h1>
              {/* <p className={cls.subtitle}>
                Review seller submission, inspect media, verify details, and
                take action.
              </p> */}
            </div>

            <div className={cls.statusCluster}>
              <span className={cls.categoryBadge}>
                {CATEGORY_LABELS[product.category] ||
                  product.category ||
                  "Product"}
              </span>
              <span className={cls.subtypeBadge}>
                {SUBTYPE_LABELS[subtypeValue] || subtypeValue}
              </span>
              <span className={cls.pendingBadge}>{moderationStatus}</span>
            </div>
          </div>

          <div className={cls.heroStats}>
            <article className={cls.statCard}>
              <span className={cls.statLabel}>Price</span>
              <strong className={cls.statValue}>
                {product.priceNoty ? `${product.priceNoty} Noty` : "-"}
              </strong>
            </article>

            <article className={cls.statCard}>
              <span className={cls.statLabel}>Media</span>
              <strong className={cls.statValue}>
                {(product.listingImages?.length || 0) +
                  (product.listingVideos?.length || 0)}
              </strong>
            </article>

            <article className={cls.statCard}>
              <span className={cls.statLabel}>Submitted</span>
              <strong className={cls.statValueSmall}>
                {formatDateTime(product.moderationSubmittedAt)}
              </strong>
            </article>

            <article className={cls.statCard}>
              <span className={cls.statLabel}>Seller</span>
              <strong className={cls.statValue}>
                {product.seller?.username
                  ? `@${product.seller.username}`
                  : "Unknown"}
              </strong>
            </article>
          </div>
        </header>

        <div className={cls.mainGrid}>
          <div className={cls.leftCol}>
            <section className={cls.panel}>
              <div className={cls.panelHeader}>
                <h3 className={cls.panelTitle}>Listing media</h3>
                <span className={cls.panelChip}>{mediaItems.length} items</span>
              </div>

              {!mediaItems.length ? (
                <div className={cls.stateCard}>No media uploaded.</div>
              ) : (
                <div className={cls.mediaGrid}>
                  {mediaItems.map((item, index) => (
                    <article key={item.id} className={cls.mediaCard}>
                      <div className={cls.mediaTopbar}>
                        <span className={cls.mediaType}>
                          {item.type === "image" ? "Image" : "Video"}
                        </span>
                        <span className={cls.mediaIndex}>#{index + 1}</span>
                      </div>

                      <div className={cls.mediaFrame}>
                        {item.type === "image" ? (
                          <img
                            src={item.url}
                            alt={product.title || "Product media"}
                            className={cls.media}
                          />
                        ) : (
                          <>
                            {item.videoId ? (
                              <VideoPlayer
                                className={cls.videoPlayer}
                                token={token}
                                videoId={item.videoId}
                                mode="proxy"
                                controls
                                muted={false}
                              />
                            ) : (
                              <VideoPlayer
                                className={cls.videoPlayer}
                                hlsMasterUrl={item.url}
                                controls
                                muted={false}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className={cls.rightCol}>
            <section className={cls.panel}>
              <div className={cls.panelHeader}>
                <h3 className={cls.panelTitle}>Seller and club</h3>
              </div>

              <div className={cls.infoStack}>
                <div className={cls.infoCard}>
                  <span className={cls.infoLabel}>Seller account</span>
                  {product.seller?.userId ? (
                    <Link
                      to={`/admin/members/single/${product.seller.userId}`}
                      className={cls.valueLink}
                    >
                      @{product.seller?.username || "unknown"}
                    </Link>
                  ) : (
                    <strong className={cls.infoValue}>Unknown seller</strong>
                  )}
                </div>

                <div className={cls.infoCard}>
                  <span className={cls.infoLabel}>Club</span>
                  {product.club ? (
                    <Link
                      to={getClubLink(product.club)}
                      className={cls.valueLink}
                    >
                      {product.club.clubName || "Untitled club"}
                    </Link>
                  ) : (
                    <strong className={cls.infoValue}>Unknown club</strong>
                  )}
                </div>
              </div>
            </section>

            <section className={cls.panel}>
              <div className={cls.panelHeader}>
                <h3 className={cls.panelTitle}>Moderation action</h3>
              </div>

              <Link
                to={`/admin/marketplace/pending-products/${product._id}/edit`}
                className={cls.editLinkButton}
              >
                Edit product details
              </Link>

              {!canModerate ? (
                <div className={cls.moderationSummaryCard}>
                  <p className={cls.moderationSummaryTitle}>
                    Status: {isApproved ? "Approved" : "Rejected"}
                  </p>
                  <p className={cls.moderationSummaryText}>
                    This product is already {moderationStatus}. Moderation
                    actions are only available while status is pending.
                  </p>
                  <div className={cls.moderationMeta}>
                    <img
                      src={moderatorAvatarUrl}
                      alt={moderator?.username || "Admin moderator"}
                      className={cls.moderatorAvatar}
                    />
                    <div className={cls.moderationMetaText}>
                      <strong>
                        {moderator?.username
                          ? `@${moderator.username}`
                          : "Admin"}
                      </strong>
                      <span>
                        {formatDateTime(product.moderationReviewedAt)}
                      </span>
                    </div>
                  </div>
                  <div className={cls.moderationNotesWrap}>
                    <span className={cls.inputLabel}>Internal notes</span>
                    <p className={cls.noteValue}>
                      {formatValue(product.moderationNotes)}
                    </p>
                  </div>
                  {isRejected ? (
                    <div className={cls.moderationNotesWrap}>
                      <span className={cls.inputLabel}>Reject reason</span>
                      <p className={cls.noteValue}>
                        {formatValue(product.moderationRejectionReason)}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {actionError ? (
                <div className={cls.errorCard}>{actionError}</div>
              ) : null}

              {canModerate ? (
                <div className={cls.actionRow}>
                  <button
                    type="button"
                    className={cls.approveButton}
                    onClick={handleApprove}
                    disabled={
                      Boolean(submitting) ||
                      isApproved ||
                      !canModerate ||
                      activeModerationForm === "reject"
                    }
                  >
                    {submitting === "approve"
                      ? "Approving..."
                      : isApproved
                        ? "Already approved"
                        : "Approve product"}
                  </button>

                  <button
                    type="button"
                    className={cls.rejectButton}
                    onClick={handleReject}
                    disabled={
                      Boolean(submitting) ||
                      isRejected ||
                      !canModerate ||
                      activeModerationForm === "approve"
                    }
                  >
                    {submitting === "reject"
                      ? "Rejecting..."
                      : isRejected
                        ? "Already rejected"
                        : "Reject product"}
                  </button>
                </div>
              ) : null}

              {canModerate && activeModerationForm === "approve" ? (
                <div className={cls.moderationForms}>
                  <div className={cls.formCard}>
                    <label className={cls.inputLabel} htmlFor="approve-notes">
                      Internal note (approval)
                    </label>
                    <textarea
                      id="approve-notes"
                      className={cls.textarea}
                      rows={3}
                      value={approveNotes}
                      onChange={(event) => setApproveNotes(event.target.value)}
                      placeholder="Optional internal note saved with approval"
                    />

                    <div className={cls.formActionRow}>
                      <button
                        type="button"
                        className={cls.approveButton}
                        onClick={handleApproveSubmit}
                        disabled={Boolean(submitting)}
                      >
                        {submitting === "approve" ? "Approving..." : "Okay"}
                      </button>
                      <button
                        type="button"
                        className={cls.cancelButton}
                        onClick={() => {
                          setActiveModerationForm("");
                          setActionError("");
                        }}
                        disabled={Boolean(submitting)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {canModerate && activeModerationForm === "reject" ? (
                <div className={cls.moderationForms}>
                  <div className={cls.formCard}>
                    <label className={cls.inputLabel} htmlFor="reject-reason">
                      Reject reason
                    </label>
                    <textarea
                      id="reject-reason"
                      className={cls.textarea}
                      rows={3}
                      value={rejectReason}
                      onChange={(event) => setRejectReason(event.target.value)}
                      placeholder="Reason shown for rejection"
                    />

                    <label
                      className={cls.inputLabel}
                      htmlFor="reject-internal-notes"
                    >
                      Internal note (rejection)
                    </label>
                    <textarea
                      id="reject-internal-notes"
                      className={cls.textarea}
                      rows={3}
                      value={rejectNotes}
                      onChange={(event) => setRejectNotes(event.target.value)}
                      placeholder="Optional internal moderation note"
                    />

                    <div className={cls.formActionRow}>
                      <button
                        type="button"
                        className={cls.rejectButton}
                        onClick={handleRejectSubmit}
                        disabled={Boolean(submitting)}
                      >
                        {submitting === "reject" ? "Rejecting..." : "Okay"}
                      </button>
                      <button
                        type="button"
                        className={cls.cancelButton}
                        onClick={() => {
                          setActiveModerationForm("");
                          setActionError("");
                        }}
                        disabled={Boolean(submitting)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          </aside>
        </div>
        <section className={cls.panel}>
          <div className={cls.panelHeader}>
            <h3 className={cls.panelTitle}>Product overview</h3>
          </div>

          <div className={cls.descriptionCard}>
            <span className={cls.sectionMiniLabel}>Description</span>
            <p className={cls.description}>
              {product.description || "No description."}
            </p>
          </div>
        </section>
        <section className={cls.panel}>
          <div className={cls.panelHeader}>
            <h3 className={cls.panelTitle}>All product fields</h3>
          </div>

          <pre className={cls.jsonBlock}>
            {JSON.stringify(product, null, 2)}
          </pre>
        </section>
      </div>
    </section>
  );
}
